import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Injectable, Injector, inject } from '@angular/core';
import { finalize, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { SKIP_PROGRESS } from './progress.context';
import { LoadingService } from 'app/core/services/loading.service';
import { AuthService } from 'app/core/auth/auth.service';

// Guard anti-loop: garante uma unica auto-recuperacao de "cookie too large" por sessao de aba.
const COOKIE_OVERFLOW_FLAG = 'oab-cookie-overflow-recovered';

@Injectable()
export class ProgressInterceptor implements HttpInterceptor {
    private injector = inject(Injector);
    private loading = inject(LoadingService);

    private pendingRequests = 0;

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const toastr = this.injector.get(ToastrService);
        // Requisicoes marcadas com SKIP_PROGRESS (ex.: login) nao alimentam o NProgress global.
        // O tratamento de erro/toast abaixo continua valendo para elas (toast so apos a resposta).
        const skipProgress = req.context.get(SKIP_PROGRESS);
        if (!skipProgress) {
            this.pendingRequests++;
            if (this.pendingRequests === 1) {
                this.loading.start();
            }
        }
        return next.handle(req).pipe(
            tap({
                next: (event: HttpEvent<any>) => {
                    if (event instanceof HttpResponse && event.status >= 200) {
                        // Requisicao voltou a passar: cookies OK, libera o guard para uma futura recuperacao.
                        if (sessionStorage.getItem(COOKIE_OVERFLOW_FLAG)) {
                            sessionStorage.removeItem(COOKIE_OVERFLOW_FLAG);
                        }
                        // finalize() controls when the global progress is completed.
                    }
                },
                error: (error: any) => {
                    if (!(error instanceof HttpErrorResponse)) {
                        return;
                    }

                    // "Request header or cookie too large": cookies legados do .oab.org.br estouraram o
                    // buffer do gateway (nginx 400/413/431/494) e a requisicao (ex.: login) foi rejeitada.
                    // Auto-recupera: limpa os cookies compartilhados e recarrega UMA vez (guard anti-loop).
                    // Substitui a acao manual do usuario de "limpar cookies do site". Suprime o toast cru.
                    if (this.isCookieOverflow(error)) {
                        if (!sessionStorage.getItem(COOKIE_OVERFLOW_FLAG)) {
                            sessionStorage.setItem(COOKIE_OVERFLOW_FLAG, '1');
                            try { this.injector.get(AuthService).clearSharedCookies(); } catch { /* best-effort */ }
                            window.location.reload();
                        }
                        return;
                    }

                    // 401/403 são tratados pelo fluxo de autenticação e pelos guards — não notificar aqui.
                    if (error.status === 401 || error.status === 403) {
                        return;
                    }

                    // Respostas 2xx/3xx que chegam como erro são falha de desserialização no cliente,
                    // não erro de servidor: tipicamente o corpo HTML da tela de login devolvido pelo
                    // gateway depois que a sessão expira (em vez de JSON). Notificar aqui gera um toast
                    // confuso "Erro! OK" (statusText="OK") no momento do logout. Ignorar.
                    if (error.status >= 200 && error.status < 400) {
                        return;
                    }

                    const payload = error.error;
                    let message: string;

                    // Formato legado de alguns endpoints OAuth: { data: { error_description } } ou { data: string }
                    if (payload && payload.data) {
                        message = payload.data.error_description
                            ? payload.data.error_description
                            : typeof payload.data === 'string'
                                ? payload.data
                                : JSON.stringify(payload.data);
                    } else {
                        // Padrão ProblemDetails (RFC 9457): a mensagem amigável fica em `detail`.
                        message =
                            payload?.detail ||
                            payload?.title ||
                            payload?.message ||
                            error.statusText ||
                            'Ocorreu um erro inesperado no sistema, por favor entrar em contato com o administrador do sistema.';
                    }

                    // 5xx e falhas de rede (status 0) → vermelho (error); demais 4xx → amarelo (warning).
                    if (error.status === 0 || error.status >= 500) {
                        toastr.error(message, 'Erro!');
                    } else {
                        toastr.warning(message, 'Erro!');
                    }
                }
            }),
            finalize(() => {
                if (skipProgress) {
                    return;
                }
                this.pendingRequests = Math.max(0, this.pendingRequests - 1);
                if (this.pendingRequests === 0) {
                    this.loading.done();
                }
            })
        );
    }

    // Detecta o erro de cabecalho/cookie grande demais. HTTP 431 e sempre esse caso; os gateways
    // (nginx) tambem podem devolver 400/413/494 com a mensagem no statusText/corpo HTML.
    private isCookieOverflow(error: HttpErrorResponse): boolean {
        if (error.status === 431) {
            return true;
        }
        const blob = `${error.statusText ?? ''} ${typeof error.error === 'string' ? error.error : ''}`.toLowerCase();
        return (
            (error.status === 400 || error.status === 413 || error.status === 494) &&
            blob.includes('too large') &&
            (blob.includes('cookie') || blob.includes('header'))
        );
    }
}
